import { MEAL_SLOT_ORDER, type MealSlot } from '@cadencia/contracts';
import { useLiveQuery } from 'dexie-react-hooks';
import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';

import {
  BotonEnlace,
  Cabecera,
  Chip,
  Estado,
  Seccion,
  Tarjeta,
} from '../../components/ui.js';
import { useAppData } from '../../data/DataProvider.js';
import { situacionDelTest, usePerfil } from '../../data/perfilConocido.js';
import { getProfile } from '../../data/repositories/profileRepo.js';
import { getDefaultGym, listEquipment } from '../../data/repositories/gymRepo.js';
import { getCardioOfDay } from '../../data/repositories/cardioRepo.js';
import { listPendingDecisions } from '../../data/repositories/decisionsRepo.js';
import {
  getActiveBlock,
  getNextSession,
  getSessionByDate,
  getSessionDetail,
  listSessionsOfWeek,
  listWeeks,
} from '../../data/repositories/trainingRepo.js';
import { daySummary } from '../../data/services/nutritionService.js';
import { formatShortDate, todayLocal } from '../../lib/dates.js';
import { Macro, NOMBRE_DE_SLOT } from '../nutricion/piezas.js';
import { IconoCheck } from '../../components/iconos.js';
import './inicio.css';

/**
 * Inicio (§4.1 de UI_UX_SPEC.md).
 *
 * ESTA PANTALLA SE QUEDÓ EN LA FASE 1
 *
 * Con el perfil y el gimnasio configurados —el estado permanente— la ruta
 * índice pintaba «Todo listo para la **Fase 2** · el circuito de entrenamiento
 * llega en la siguiente fase». Las ocho fases cerraron. Era la primera pantalla
 * de la aplicación diciendo que lo que hay debajo todavía no existe, y ningún
 * test tocaba esa rama, así que nadie la veía salvo el usuario. Es la trampa de
 * CLAUDE.md: **un «llega en la Fase N» sobrevive a la Fase N**.
 *
 * Lo que faltaba no era el texto: era la pantalla. `--texto-cifra: 56px` estaba
 * en `tokens.css` desde la Fase 1 sin que lo usara nadie, definido para la cifra
 * grande de este boceto.
 *
 * QUÉ SE PINTA, Y POR QUÉ ESTE ORDEN
 *
 * El §4.1 fija cuatro bloques y un máximo de cinco: objetivo del día, entreno
 * de hoy, comidas y avisos —estos solo si existen—. El orden no es estético:
 * arriba va lo que cambia varias veces al día y se consulta de pasada; el aviso
 * de una propuesta pendiente va al final porque interrumpe, y lo que interrumpe
 * no se pone donde se mira por costumbre.
 *
 * Sin agua, sin racha, sin puntos (decisión expresa del usuario).
 *
 * UNA SOLA CONSULTA VIVA
 *
 * Las demás pantallas usan un `useLiveQuery` por dato. Aquí son seis lecturas
 * que dependen todas de lo mismo —el usuario y el día de hoy— y que se pintan
 * juntas o no se pintan: repartirlas daría seis estados de carga distintos y una
 * pantalla que aparece a trozos. Una sola consulta da un estado de carga y una
 * foto coherente, y Dexie la reejecuta igual en cuanto cambie cualquiera de las
 * tablas que ha leído.
 */

function fechaLarga(date: Date): string {
  return date.toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/*
 * El orden de los huecos sale de `MEAL_SLOT_ORDER`, no de una copia local.
 *
 * Aquí había una lista escrita a mano con cinco valores, y cuando el 08/09/2026
 * entró la merienda se quedó sin ella: esta pantalla habría filtrado las
 * comidas del día contra una lista que no la contiene, así que la merienda no
 * se habría pintado en Inicio. Sin error, sin aviso: un hueco de menos.
 */

interface SesionDeInicio {
  id: string;
  nombre: string;
  ejercicios: number;
  minutos: number;
  fecha: string;
  /** Hoy, una futura, o una de esta semana que se quedo sin hacer. */
  cuando: 'hoy' | 'proxima' | 'atrasada';
  /** Cuantas quedan atrasadas esta semana. 0 salvo en `cuando: 'atrasada'`. */
  atrasadas: number;
}

interface DatosDeInicio {
  nombre: string | null;
  faltaPerfil: boolean;
  faltaGimnasio: boolean;
  objetivoKcal: number | undefined;
  consumidas: { kcal: number; proteinG: number; carbsG: number; fatG: number };
  objetivoMacros: { proteinG: number; carbsG: number; fatG: number } | undefined;
  slotsPlanificados: MealSlot[];
  slotsRegistrados: MealSlot[];
  sesion: SesionDeInicio | null;
  /** Distinguir «no hay bloque» de «esta semana ya esta hecha». */
  hayBloque: boolean;
  /** Hoy tocaba y ya esta hecho: no es lo mismo que un dia de descanso. */
  hechoHoy: boolean;
  cardioMinutos: number | null;
  pendientes: number;
}

export function InicioPage(): ReactNode {
  const { ctx, ready } = useAppData();
  const userId = ctx?.userId;
  const estadoPerfil = usePerfil(userId ?? null);

  const datos = useLiveQuery<DatosDeInicio | undefined>(async () => {
    if (userId === undefined) return undefined;
    const hoy = todayLocal();

    const perfil = await getProfile(userId);
    const gym = await getDefaultGym(userId);
    const equipamiento = gym ? await listEquipment(gym.id) : [];

    // La sesión de hoy manda; si hoy no toca, la siguiente que quede por hacer.
    // Enseñar solo la de hoy dejaría la tarjeta vacía tres días de cada siete en
    // un plan de cuatro sesiones, que es justo cuando hace falta recordar cuándo
    // vuelves.
    /*
     * La de hoy manda, PERO solo si queda algo que hacer con ella.
     *
     * `getSessionByDate` devuelve la sesion del dia sea cual sea su estado, y
     * `deHoy` tenia prioridad sobre todo lo demas: al terminar el entrenamiento
     * esta pantalla seguia diciendo «Entreno de hoy · Empezar entrenamiento»
     * sobre una sesion ya cerrada, y la siguiente no aparecia hasta el dia
     * siguiente. Inicio contesta «que tengo que hacer ahora» (§4.1): una sesion
     * hecha no es una respuesta.
     *
     * El sintoma era un test rojo en `main` —«sin sesion hoy enseña la
     * proxima»— que solo fallaba lunes, martes, jueves y viernes, porque son
     * los dias en los que el bloque de readaptacion tiene sesion. Otra vez la
     * dependencia del calendario que este mismo fichero documenta.
     */
    const sesionDeHoy = await getSessionByDate(userId, hoy);
    const pendienteHoy =
      sesionDeHoy !== undefined &&
      (sesionDeHoy.status === 'planned' || sesionDeHoy.status === 'in_progress');
    const deHoy = pendienteHoy ? sesionDeHoy : undefined;
    const siguiente = deHoy ?? (await getNextSession(userId));

    /**
     * Y si no hay ni de hoy ni futura, las que se quedaron atras (R-20).
     *
     * `getNextSession` solo mira de hoy en adelante. Un sabado, con las cuatro
     * sesiones de la semana ya en el pasado, esta tarjeta caia en «Aun no hay
     * plan de entrenamiento · Crear el bloque» **con el bloque activo delante**:
     * el consejo mandaba a crear lo que ya existe (R-24). Es el mismo hueco que
     * se cerro en Entrenar el 02/09/2026 y que aqui nacio abierto, porque esta
     * pantalla se construyo al dia siguiente sin mirar aquella.
     */
    const bloque = await getActiveBlock(userId);
    const semanas = bloque ? await listWeeks(bloque.id) : [];
    const semanaActiva = semanas.find((semana) => semana.status === 'active') ?? semanas[0];
    const atrasadas = semanaActiva
      ? (await listSessionsOfWeek(semanaActiva.id)).filter(
          (fila) => fila.scheduledDate < hoy && fila.status === 'planned',
        )
      : [];

    const sesion = siguiente ?? atrasadas[0];
    const detalle = sesion ? await getSessionDetail(sesion.id) : null;

    const resumen = await daySummary(userId, hoy);
    const cardio = await getCardioOfDay(userId, hoy);
    const pendientes = await listPendingDecisions(userId);

    return {
      nombre: perfil?.displayName ?? null,
      faltaPerfil: perfil === undefined,
      faltaGimnasio: equipamiento.length === 0,
      objetivoKcal: resumen.target?.kcal,
      consumidas: resumen.consumed,
      objetivoMacros: resumen.target
        ? {
            proteinG: resumen.target.proteinG,
            carbsG: resumen.target.carbsG,
            fatG: resumen.target.fatG,
          }
        : undefined,
      slotsPlanificados: resumen.meals.map((comida) => comida.slot),
      slotsRegistrados: [...resumen.loggedSlots],
      sesion:
        sesion && detalle
          ? {
              id: sesion.id,
              nombre: sesion.name,
              ejercicios: detalle.exercises.length,
              minutos: sesion.estimatedMinutes,
              fecha: sesion.scheduledDate,
              cuando:
                sesion.scheduledDate === hoy
                  ? 'hoy'
                  : sesion.scheduledDate < hoy
                    ? 'atrasada'
                    : 'proxima',
              atrasadas: siguiente ? 0 : atrasadas.length,
            }
          : null,
      hayBloque: bloque !== undefined,
      hechoHoy: sesionDeHoy !== undefined && sesionDeHoy.status === 'completed',
      cardioMinutos: cardio ? cardio.targetMinutes : null,
      pendientes: pendientes.length,
    };
  }, [userId]);

  if (!ready || datos === undefined || estadoPerfil === undefined) {
    return <Estado tipo="cargando" titulo="Cargando tus datos" />;
  }

  /*
   * La aplicación empieza por el test inicial (docs/TEST_INICIAL.md §2.5).
   *
   * Solo cuando SE SABE que no hay perfil. Antes esta pantalla decidía con
   * `perfil === undefined`, que en un dispositivo recién estrenado significa
   * «no lo he mirado»: mandaba a crear un perfil a quien ya lo tenía en el
   * servidor. Con el test, eso sería repetirlo entero y crear un segundo bloque
   * encima del suyo (`perfilConocido.ts`).
   */
  const situacion = situacionDelTest(estadoPerfil);
  if (situacion === 'sin_perfil') return <Navigate to="/bienvenida" replace />;
  if (situacion === 'desconocido' && datos.faltaPerfil) {
    return (
      <Estado
        tipo="cargando"
        titulo="Sincronizando tus datos"
        descripcion="Este dispositivo todavía no ha recibido tu perfil. Si ya lo tienes, aparecerá en cuanto llegue."
      />
    );
  }

  const cabecera = (
    <Cabecera
      encima={fechaLarga(new Date())}
      titulo={datos.nombre === null ? 'Bienvenido' : `Hola, ${datos.nombre}`}
    />
  );

  // --- Primera vez: falta el gimnasio ----------------------------------------
  // El perfil ya no: sin él, la aplicación empieza por el test (arriba).
  if (datos.faltaGimnasio) {
    return (
      <>
        {cabecera}
        <Tarjeta
          tono="focal"
          titulo="Configura tu gimnasio"
          descripcion="Sin saber qué cargas puedes seleccionar de verdad, no se te recomendará ninguna."
          acciones={<BotonEnlace a="/mas/gimnasio">Añadir equipamiento</BotonEnlace>}
        />
      </>
    );
  }

  // Un perfil de antes del test: se invita, no se obliga. Ese atleta ya tiene
  // su plan, y el test solo afina el siguiente.
  const invitacionAlTest =
    situacion === 'sin_test' ? (
      <Tarjeta
        tono="focal"
        titulo="Cuéntame cómo es tu semana"
        descripcion="Qué días puedes entrenar, cuánto tiempo tienes y si alguna articulación te molesta. Con eso ajusto tus próximos bloques."
        acciones={<BotonEnlace a="/bienvenida">Hacer el test</BotonEnlace>}
      />
    ) : null;

  const objetivoKcal = datos.objetivoKcal;
  const restantes =
    objetivoKcal === undefined ? 0 : Math.round(objetivoKcal - datos.consumidas.kcal);
  /*
   * Pasarse del objetivo se dice, no se esconde: un negativo es informacion, y
   * cortarlo en cero seria mentir justo en la unica cifra que se lee de lejos.
   * Lo que si cambia es el TONO —ambar, no verde— y la palabra: «1.842 kcal
   * restantes» y «120 kcal de mas» son dos situaciones distintas, y un signo
   * menos delante de una cifra de 56 px no es forma de distinguirlas.
   */
  const pasado = restantes < 0;
  const porcentaje =
    objetivoKcal === undefined || objetivoKcal <= 0
      ? 0
      : Math.min(100, (datos.consumidas.kcal / objetivoKcal) * 100);
  const huecos = MEAL_SLOT_ORDER.filter((slot) => datos.slotsPlanificados.includes(slot));

  return (
    <>
      {cabecera}
      {invitacionAlTest}

      {/*
        Dos columnas en escritorio, apiladas en movil, y el orden del DOM es ya
        el del §4.1: lo que se hace hoy a la izquierda, lo que se consulta de
        pasada a la derecha. En movil se lee en ese mismo orden sin una sola
        regla mas.
      */}
      <div className="columnas">
        <div className="columnas__principal">
          {/* 1 · Tu objetivo de hoy */}
          {objetivoKcal === undefined ? (
            <Seccion titulo="Tu objetivo de hoy">
              <Estado
                tipo="vacio"
                compacto
                titulo="Aún no hay menú para esta semana"
                descripcion="Los objetivos del día salen del menú: se genera con tu peso, tu objetivo y lo que tengas entrenado."
                accion={<BotonEnlace a="/plan/nutricion">Generar el menú</BotonEnlace>}
              />
            </Seccion>
          ) : (
            <Tarjeta tono="focal" className="objetivo">
              <h2 className="t-etiqueta">Tu objetivo de hoy</h2>
              <p className={`objetivo__cifra${pasado ? ' objetivo__cifra--pasado' : ''}`}>
                <span className="objetivo__valor numeros">{Math.abs(restantes)}</span>
                <span className="objetivo__unidad">
                  {pasado ? 'kcal de más' : 'kcal restantes'}
                </span>
              </p>
              <div className="objetivo__barra" aria-hidden="true">
                <span style={{ width: `${porcentaje}%` }} />
              </div>
              <p className="objetivo__detalle">
                {Math.round(datos.consumidas.kcal)} de {Math.round(objetivoKcal)} consumidas
              </p>
              <div className="macros">
                <Macro
                  etiqueta="proteína"
                  actual={datos.consumidas.proteinG}
                  objetivo={datos.objetivoMacros?.proteinG}
                  unidad="g"
                />
                <Macro
                  etiqueta="hidratos"
                  actual={datos.consumidas.carbsG}
                  objetivo={datos.objetivoMacros?.carbsG}
                  unidad="g"
                />
                <Macro
                  etiqueta="grasa"
                  actual={datos.consumidas.fatG}
                  objetivo={datos.objetivoMacros?.fatG}
                  unidad="g"
                />
              </div>
            </Tarjeta>
          )}

          {/* 2 · Entreno de hoy */}
          {datos.sesion === null ? (
            <Seccion titulo="Entreno">
              {/*
                Tres vacíos distintos, y decirlos con la misma frase mandaba a
                la pantalla equivocada (R-20 y R-24). Sin bloque, lo que falta
                es crearlo; con el bloque puesto y la semana terminada, no falta
                nada y lo útil es decir cuándo vuelve a haber algo; y si lo de
                hoy ya está hecho, eso es una respuesta y no un hueco. La semana
                siguiente NO se materializa por adelantado a propósito: se
                construiría con los `volumeTargets` de hoy y una propuesta
                aprobada el domingo ya no entraría en ella.
              */}
              {!datos.hayBloque ? (
                <Estado
                  tipo="primera-vez"
                  compacto
                  titulo="Aún no hay plan de entrenamiento"
                  descripcion="El bloque decide qué se entrena cada día y con qué cargas."
                  accion={<BotonEnlace a="/entrenar">Crear el bloque</BotonEnlace>}
                />
              ) : datos.hechoHoy ? (
                <Estado
                  tipo="vacio"
                  compacto
                  titulo="Entreno de hoy, hecho"
                  descripcion="No queda ninguna sesión pendiente esta semana. La siguiente se prepara en Entrenar en cuanto esta termina."
                />
              ) : (
                <Estado
                  tipo="vacio"
                  compacto
                  titulo="Hoy toca descansar"
                  descripcion="No queda ninguna sesión pendiente esta semana. La siguiente se prepara en Entrenar en cuanto esta termina."
                />
              )}
            </Seccion>
          ) : (
            <Tarjeta className="entreno">
              <h2 className="entreno__rotulo t-etiqueta">
                {datos.sesion.cuando === 'hoy'
                  ? 'Entreno de hoy'
                  : datos.sesion.cuando === 'atrasada'
                    ? datos.sesion.atrasadas === 1
                      ? 'Te queda una sesión de esta semana'
                      : `Te quedan ${datos.sesion.atrasadas} sesiones de esta semana`
                    : `Próximo entreno · ${formatShortDate(datos.sesion.fecha)}`}
              </h2>
              <p className="entreno__nombre">{datos.sesion.nombre}</p>
              <p className="entreno__meta">
                {datos.sesion.ejercicios} ejercicios · ~{datos.sesion.minutos} min
                {datos.cardioMinutos !== null && ` · cardio ${datos.cardioMinutos} min`}
              </p>
              <BotonEnlace
                a={`/entrenar/sesion/${datos.sesion.id}`}
                ancho="completo"
                className="entreno__accion"
              >
                {datos.sesion.cuando === 'hoy' ? 'Empezar entrenamiento' : 'Ver la sesión'}
              </BotonEnlace>
            </Tarjeta>
          )}
        </div>

        <div className="columnas__lateral">
          {/* 3 · Comidas */}
          {huecos.length > 0 && (
            <Seccion
              titulo="Comidas"
              acciones={
                <BotonEnlace a="/plan/nutricion" variante="texto">
                  Ver el día
                </BotonEnlace>
              }
            >
              <ul className="huecos">
                {huecos.map((slot) => {
                  const hecho = datos.slotsRegistrados.includes(slot);
                  return (
                    <li
                      key={slot}
                      className={`hueco ${hecho ? 'hueco--hecho' : 'hueco--pendiente'}`}
                    >
                      <span className="hueco__marca" aria-hidden="true">
                        {hecho ? <IconoCheck tam={13} /> : null}
                      </span>
                      {NOMBRE_DE_SLOT[slot]}
                      <span className="visualmente-oculto">
                        {hecho ? ' registrada' : ' sin registrar'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Seccion>
          )}

          {/* 4 · Avisos, solo si existen */}
          {datos.pendientes > 0 && (
            <Tarjeta
              tono="aviso"
              titulo="Tienes cambios por aprobar"
              acciones={
                <BotonEnlace a="/mas/propuestas" variante="secundario">
                  Revisarlos
                </BotonEnlace>
              }
            >
              <p className="pista">
                <Chip tono="aviso">
                  {datos.pendientes === 1
                    ? '1 propuesta pendiente'
                    : `${datos.pendientes} propuestas pendientes`}
                </Chip>{' '}
                Caducan solas: una propuesta vieja se decidió con datos que ya no son los tuyos.
              </p>
            </Tarjeta>
          )}
        </div>
      </div>
    </>
  );
}
